import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import WasslaLogo from '../components/WasslaLogo';
import AppHeader from '../components/AppHeader';
import { 
  ArrowLeft, 
  ArrowRight, 
  Mail, 
  MessageCircle, 
  ChevronDown, 
  ChevronUp,
  Send,
  Copy,
  Check,
  HelpCircle,
  Shield,
  FileText,
  Sparkles,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORT_EMAIL } from '../data/legalContent';
import { useAppVersion } from '../version';
import { 
  trackHelpPageView, 
  trackFaqOpen, 
  trackContactClick, 
  trackCopyClick 
} from '../utils/helpAnalytics';
import './Contact.css';

// WhatsApp icon component
const WhatsAppIcon = ({ size = 24, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

function Contact() {
  const { t, i18n } = useTranslation();
  const { label: appVersionLabel } = useAppVersion();
  const navigate = useNavigate();
  const goBack = useSmartBack('/account');
  const { user } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;
  
  const [copiedItem, setCopiedItem] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Track page view on mount
  useEffect(() => {
    trackHelpPageView();
  }, []);

  // Prefill name + email for logged-in users. Users remain free to edit
  // either field before submitting the support request.
  useEffect(() => {
    if (!user) return;
    setFormData(prev => ({
      ...prev,
      name: prev.name || user.name || '',
      email: prev.email || user.email || '',
    }));
  }, [user]);

  // Contact methods — clarified labels so users can distinguish channels:
  //   Email card     = direct email contact
  //   WhatsApp card  = quick chat / instant response
  //   Form section   = structured support request sent by email (handled below)
  const contactMethods = [
    { 
      type: 'email',
      value: SUPPORT_EMAIL, 
      label: t('cont.emailSupport'),
      description: t('cont.responseWithin24Hours'),
      icon: Mail,
      href: `mailto:${SUPPORT_EMAIL}`,
      color: '#1F6F3A',
      bgColor: 'rgba(31, 111, 58, 0.1)'
    },
    { 
      type: 'whatsapp',
      value: '+249 92 786 1753', 
      copyValue: '+249927861753',
      label: t('cont.whatsappSupport'),
      description: t('cont.instantResponse'),
      icon: WhatsAppIcon,
      href: 'https://wa.me/249927861753',
      color: '#25D366',
      bgColor: 'rgba(37, 211, 102, 0.1)'
    }
  ];

  // Quick action buttons
  const quickActions = [
    {
      icon: HelpCircle,
      label: t('cont.faqs'),
      action: () => document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' }),
      color: '#FF8A00'
    },
    {
      icon: Shield,
      label: t('cont.privacyPolicy'),
      action: () => navigate('/privacy-policy'),
      color: '#1F6F3A'
    },
    {
      icon: FileText,
      label: t('cont.termsOfUse'),
      action: () => navigate('/terms'),
      color: '#6B7280'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      id: 'account',
      question: t('cont.howDoICreateA'),
      answer: t('cont.youCanCreateANew')
    },
    {
      id: 'post-ad',
      question: t('cont.howDoIPostAn'),
      answer: t('cont.afterLoggingInTapThe')
    },
    {
      id: 'contact-seller',
      question: t('cont.howDoIContactA'),
      answer: t('cont.tapTheContactSellerButton')
    },
    {
      id: 'delete-account',
      question: t('cont.howDoIDeleteMy'),
      // Structured answer (rendered as ordered list + warning). The render
      // path below special-cases this id; legacy `answer` string kept for
      // any analytics/snapshot tooling still reading it.
      answer: t('cont.goToSettingsAccountDelete'),
      richContent: {
        intro: t('cont.deleteAccount.intro'),
        steps: [
          t('cont.deleteAccount.step1'),
          t('cont.deleteAccount.step2'),
          t('cont.deleteAccount.step3'),
          t('cont.deleteAccount.step4'),
        ],
        warning: t('cont.deleteAccount.warning'),
        support: t('cont.deleteAccount.support'),
      },
    },
    {
      id: 'report',
      question: t('cont.howDoIReportA'),
      answer: t('cont.openTheAdTapReport')
    }
  ];

  const handleCopy = async (value, contactType) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedItem(value);
      toast.success(t('cont.copied'));
      // Track copy event
      trackCopyClick(contactType);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const toggleFaq = (id) => {
    const newExpanded = expandedFaq === id ? null : id;
    setExpandedFaq(newExpanded);
    // Track FAQ open event
    if (newExpanded) {
      trackFaqOpen(id);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // Submit through the unified support email pipeline on the backend.
    // Destination: Wassla.connect@gmail.com via Resend. Reply-To is set to
    // the user's own email so support can reply directly. No WhatsApp is
    // opened from this form — it is strictly email-based.
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });
      if (res.status === 429) {
        toast.info(t('cont.duplicateRequest'));
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'request_failed');
      }
      setSent(true);
      toast.success(t('cont.supportRequestSent'));
    } catch (error) {
      console.error('[Contact] support submit failed:', error);
      toast.error(t('cont.errorPleaseTryAgain'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="support-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="contact-page">
        <div className="support-success-card">
          <div className="success-icon-wrapper">
            <CheckCircle size={48} />
          </div>
          <h2 data-testid="support-success-title">{t('cont.supportRequestSent')}</h2>
          <p data-testid="support-success-note">{t('cont.supportRequestReplyNote')}</p>
          <button 
            className="btn btn-primary"
            onClick={goBack}
            data-testid="back-to-account"
          >
            {t('cont.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="contact-page">
      <AppHeader
        title={t('cont.helpSupport')}
        onBack={goBack}
        testId="contact-header"
      />

      {/* Hero Section with Logo */}
      <div className="support-hero">
        <div className="support-logo-wrapper">
          <WasslaLogo size="large" className="support-logo" />
        </div>
        <h1 className="support-title">
          {t('cont.helpSupport')}
        </h1>
        <p className="support-subtitle">
          {t('cont.wereHereToHelpYou')}
        </p>
      </div>

      {/* Quick Action Buttons */}
      <div className="support-quick-actions">
        {quickActions.map((action, idx) => {
          const IconComponent = action.icon;
          return (
            <button
              key={idx}
              className="quick-action-btn"
              onClick={action.action}
              style={{ '--action-color': action.color }}
              data-testid={`quick-action-${idx}`}
            >
              <IconComponent size={20} />
              <span>{action.label}</span>
            </button>
          );
        })}
      </div>

      {/* Contact Cards */}
      <div className="support-section">
        <h2 className="support-section-title">
          <MessageCircle size={20} />
          <span>{t('cont.contactUs')}</span>
        </h2>
        
        <div className="contact-cards">
          {contactMethods.map((method, idx) => {
            const IconComponent = method.icon;
            return (
              <div 
                key={idx} 
                className="contact-card"
                style={{ '--card-color': method.color, '--card-bg': method.bgColor }}
              >
                <a 
                  href={method.href}
                  className="contact-card-main"
                  target={method.type === 'whatsapp' ? '_blank' : '_self'}
                  rel={method.type === 'whatsapp' ? 'noopener noreferrer' : ''}
  data-testid={`contact-${method.type}`}
                  onClick={() => trackContactClick(method.type)}
                >
                  <div className="contact-card-icon">
                    <IconComponent size={24} />
                  </div>
                  <div className="contact-card-info">
                    <span className="contact-card-label">{method.label}</span>
                    <span className="contact-card-value" dir="ltr">{method.value}</span>
                    <span className="contact-card-desc">{method.description}</span>
                  </div>
                </a>
                <button 
                  className="contact-card-copy"
                  onClick={() => handleCopy(method.copyValue || method.value, method.type)}
                  data-testid={`copy-${method.type}`}
                  aria-label={t('cont.copy')}
                >
                  {copiedItem === method.value ? (
                    <Check size={18} className="copy-success" />
                  ) : (
                    <Copy size={18} />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Send Message Toggle Button */}
        <button 
          className="send-message-toggle"
          onClick={() => setShowForm(!showForm)}
          data-testid="toggle-form"
        >
          <Send size={18} />
          <span>{t('cont.sendAMessage')}</span>
          <ChevronDown size={18} className={showForm ? 'rotate' : ''} />
        </button>

        {/* Contact Form */}
        {showForm && (
          <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
            {/* Helper text — makes it explicit that this form submits an
                email to support, so users don't confuse it with WhatsApp. */}
            <div className="contact-form-helper" data-testid="contact-form-helper">
              <p className="contact-form-helper-line">{t('cont.supportFormHelper1')}</p>
              <p className="contact-form-helper-line">{t('cont.supportFormHelper2')}</p>
              <p className="contact-form-helper-line contact-form-helper-spam" data-testid="contact-form-helper-spam">
                {t('cont.supportFormHelper3')}
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="name">{t('cont.name')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="support-form-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('cont.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="support-form-email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">{t('cont.subject')}</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="support-form-subject"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">{t('cont.message')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="form-input form-textarea"
                data-testid="support-form-message"
              />
            </div>

            <button 
              type="submit" 
              className="form-submit-btn"
              disabled={sending}
              data-testid="support-form-submit"
            >
              {sending ? (
                t('cont.sending')
              ) : (
                <>
                  <Send size={18} />
                  <span>{t('cont.send')}</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>

      {/* FAQ Section */}
      <div className="support-section" id="faq-section">
        <h2 className="support-section-title">
          <HelpCircle size={20} />
          <span>{t('cont.frequentlyAskedQuestions')}</span>
        </h2>
        
        <div className="faq-list">
          {faqItems.map((faq) => (
            <div 
              key={faq.id} 
              className={`faq-item ${expandedFaq === faq.id ? 'expanded' : ''}`}
            >
              <button 
                className="faq-question"
                onClick={() => toggleFaq(faq.id)}
                data-testid={`faq-${faq.id}`}
              >
                <span>{faq.question}</span>
                {expandedFaq === faq.id ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>
              {expandedFaq === faq.id && (
                <div className="faq-answer" data-testid={`faq-answer-${faq.id}`}>
                  {faq.richContent ? (
                    <>
                      <p className="faq-intro">{faq.richContent.intro}</p>
                      <ol className="faq-steps">
                        {faq.richContent.steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                      <div
                        className="faq-warning"
                        role="alert"
                        data-testid={`faq-warning-${faq.id}`}
                      >
                        <AlertTriangle size={16} aria-hidden="true" />
                        <span>{faq.richContent.warning}</span>
                      </div>
                      {faq.richContent.support && (
                        <p className="faq-support-note">{faq.richContent.support}</p>
                      )}
                    </>
                  ) : (
                    <p>{faq.answer}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="support-footer">
        <p>
          {t('cont.wasslaClassifiedsPlatform')}
        </p>
        <p className="support-footer-version" data-testid="support-footer-version">{appVersionLabel}</p>
      </div>
    </div>
  );
}

export default Contact;
